import { useState } from 'react';
import { CallbackBand, Footer, PageHero } from '../components/Layout.jsx';
import { Alert, Button } from '../components/ui.jsx';
import { submitEnquiry, ApiError } from '../lib/api.js';
import { getAttribution, getSessionUid } from '../lib/attribution.js';
import { CONTACT_DETAILS } from '../lib/siteContent.js';
import { ukPhoneError } from '../lib/phone.js';

const ENQUIRY_TYPES = [
  'Home Broadband',
  'Business Broadband',
  'Existing enquiry',
  'Complaint',
  'Something else',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(values) {
  const errors = {};
  if (!values.firstName.trim()) errors.firstName = 'Please enter your first name';
  if (!values.lastName.trim()) errors.lastName = 'Please enter your last name';
  if (!values.email.trim()) errors.email = 'Please enter your email address';
  else if (!EMAIL_RE.test(values.email.trim())) errors.email = 'Please enter a valid email address';
  if (!values.message.trim()) errors.message = 'Please tell us how we can help';
  // Optional here — but if they give us a number it has to be one we can call
  // back on, and the rule is the same as the journey's.
  if (values.phone.trim()) {
    const phoneError = ukPhoneError(values.phone);
    if (phoneError) errors.phone = phoneError;
  }
  return errors;
}

/** One labelled control. The contact form's fields are shorter than the
 *  journey's (52px against 56px) and its labels are small caps, so it does not
 *  reuse the journey's TextField. */
function Field({ label, error, children }) {
  return (
    <div className="enquiry__field">
      <label className="enquiry__label" htmlFor={children.props.id}>
        {label}
      </label>
      {children}
      {error && <span className="field__error">{error}</span>}
    </div>
  );
}

export default function ContactPage() {
  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    enquiryType: ENQUIRY_TYPES[0],
    message: '',
    // Honeypot. Hidden from real users; bots fill it in.
    website: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [sendError, setSendError] = useState(null);

  const set = (field) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) {
      document.querySelector(`[name="${Object.keys(found)[0]}"]`)?.focus();
      return;
    }

    setStatus('sending');
    setSendError(null);
    try {
      await submitEnquiry({
        ...values,
        sessionUid: getSessionUid(),
        attribution: getAttribution(),
        website: values.website || undefined,
      });
      setStatus('sent');
    } catch (err) {
      setStatus('idle');
      setSendError(
        err instanceof ApiError
          ? err.message
          : 'We could not send your message. Please call us instead.',
      );
    }
  };

  return (
    <div className="page" data-brand="home">
      <PageHero
        title="Contact Us"
        subtitle="Fill in our contact form below to get in touch."
      />

      <main className="page__main">
        <section className="contact">
          <div className="section-header">
            <span className="eyebrow">Contact</span>
            <h2 className="section-header__title">Get In Touch With Us.</h2>
            <p className="section-header__subtitle">
              Have a question about switching your broadband? Fill in the form below and our team
              will get back to you within 24 hours.
            </p>
          </div>

          <div className="contact__layout">
            <div className="contact__details">
              <div className="details-card">
                <h3 className="details-card__title">Contact details</h3>
                <div className="details-card__row">
                  <span className="details-card__key">Phone</span>
                  <a className="details-card__value" href={`tel:${CONTACT_DETAILS.phoneHref}`}>
                    {CONTACT_DETAILS.phone}
                  </a>
                </div>
                <div className="details-card__row">
                  <span className="details-card__key">Email</span>
                  <a className="details-card__value" href={`mailto:${CONTACT_DETAILS.email}`}>
                    {CONTACT_DETAILS.email}
                  </a>
                </div>
                <div className="details-card__row">
                  <span className="details-card__key">Office hours</span>
                  <span className="details-card__value">{CONTACT_DETAILS.hours}</span>
                </div>
              </div>

              <div className="details-card">
                <h3 className="details-card__title">Registered Address</h3>
                <div className="details-card__row">
                  <span className="details-card__key">Our address for all correspondence</span>
                  <address className="details-card__value details-card__value--address">
                    {CONTACT_DETAILS.address}
                  </address>
                </div>
              </div>
            </div>

            <div className="card enquiry">
              {status === 'sent' ? (
                <div className="enquiry__done" role="status">
                  <h3 className="card__title">Thanks — we’ve got that</h3>
                  <p>
                    Our team will come back to you within 24 hours. If it is urgent, call us on{' '}
                    <a href={`tel:${CONTACT_DETAILS.phoneHref}`}>{CONTACT_DETAILS.phone}</a>.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  {sendError && <Alert>{sendError}</Alert>}

                  <div className="enquiry__row">
                    <Field label="First Name" error={errors.firstName}>
                      <input
                        id="contact-first"
                        name="firstName"
                        className="enquiry__control"
                        placeholder="Enter your first name"
                        value={values.firstName}
                        onChange={set('firstName')}
                        autoComplete="given-name"
                      />
                    </Field>
                    <Field label="Last Name" error={errors.lastName}>
                      <input
                        id="contact-last"
                        name="lastName"
                        className="enquiry__control"
                        placeholder="Enter your last name"
                        value={values.lastName}
                        onChange={set('lastName')}
                        autoComplete="family-name"
                      />
                    </Field>
                  </div>

                  <div className="enquiry__row">
                    <Field label="Email Address" error={errors.email}>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        className="enquiry__control"
                        placeholder="you@example.com"
                        value={values.email}
                        onChange={set('email')}
                        autoComplete="email"
                      />
                    </Field>
                    <Field label="Phone Number" error={errors.phone}>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        className="enquiry__control"
                        placeholder="07700 900123"
                        value={values.phone}
                        onChange={set('phone')}
                        autoComplete="tel"
                      />
                    </Field>
                  </div>

                  <Field label="Enquiry Type">
                    <select
                      id="contact-type"
                      name="enquiryType"
                      className="enquiry__control enquiry__control--select"
                      value={values.enquiryType}
                      onChange={set('enquiryType')}
                    >
                      {ENQUIRY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Message" error={errors.message}>
                    <textarea
                      id="contact-message"
                      name="message"
                      className="enquiry__control enquiry__control--area"
                      placeholder="Tell us what you need help with..."
                      rows={6}
                      value={values.message}
                      onChange={set('message')}
                    />
                  </Field>

                  {/* Honeypot — off-screen rather than display:none, which some bots skip. */}
                  <div className="visually-hidden" aria-hidden="true">
                    <label htmlFor="contact-website">Leave this field empty</label>
                    <input
                      id="contact-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={values.website}
                      onChange={set('website')}
                    />
                  </div>

                  <Button type="submit" loading={status === 'sending'}>
                    Submit Enquiry
                  </Button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <CallbackBand brand="home" />
      <Footer />
    </div>
  );
}
